/**
 * Gameplay tuning, shared by all editions.
 *
 * All distances are in world units (WU): the visible height of the game is always
 * 1000 WU, independent of the pixel resolution. The width follows the aspect ratio
 * (16:9 projection ≈ 1778 WU, portrait phone ≈ 460–560 WU). Times are in seconds,
 * speeds in WU/s, accelerations in WU/s².
 */
import { TextureKey } from '../assets/manifest.js';

export const WORLD_HEIGHT = 1000;

export const TUNING = {
  bird: {
    /** horizontal position as fraction of the visible width … */
    xFraction: 0.28,
    /** … but never further right than this (wide projection screens) */
    maxX: 560,
    /** start height (fraction of the playfield above the ground) */
    startYFraction: 0.45,
    /** artwork: one spritesheet frame (the fox in flight ≈ 170 × 125 WU in the middle of it) */
    displayWidth: 210,
    displayHeight: 189,
    /** distance from the sprite centre down to the paws – where the figure comes to rest on the ground */
    footOffset: 62,
    /**
     * Collision: circles at these vertical offsets from the centre, clearly smaller than the
     * artwork – forgiving with input delay.
     */
    hitRadius: 30,
    hitOffsets: [0],
    /**
     * Tap = upward impulse (a jump in the air), otherwise the figure sinks. Gravity and fall
     * speed are much lower than in classic Flappy Bird (2600 / 1100): floatier, slower
     * descent, more time to react to the input delay.
     */
    gravity: 1500,
    /** upward speed after a tap (young) */
    flapVelocity: 600,
    maxFallSpeed: 620,
    /** idle hovering in ATTRACT / READY */
    bobAmplitude: 14,
    bobSpeed: 4,
    /** rotation (degrees) when rising / falling at max speed */
    angleUp: -10,
    angleDown: 18,
    /** spritesheet animation (frames see objects/Bird.ts) */
    anim: {
      /** flight loop, the legs paddle (idle and gliding at the top of an arc) */
      floatFps: 4,
      /** hop pose (legs pulled up) right after a tap */
      kickSec: 0.15,
      /** impact pose after a hit */
      hitSec: 0.6,
      /** joy pose after a coin */
      joySec: 0.35,
      /** pose by vertical speed (WU/s, negative = up): rising faster than this = hop pose … */
      riseBelowVy: -150,
      /** … sinking faster than this = surprised pose, in between = flight */
      fallAboveVy: 450,
    },
  },
  /**
   * One round = one working life: the bird starts young and is old (retirement age)
   * exactly when the progress bar is full. Age makes it visibly older and less agile.
   */
  life: {
    /** age shown at the start and at the end of the progress bar */
    startAge: 20,
    endAge: 67,
    /** agility at the end of life (1 = young). Scales the flap speed: an old bird hops lower. */
    agilityOld: 0.75,
    /** > 1: agility stays high for longer and drops late in life */
    agingCurve: 1.5,
    /** artwork per age group: from this age on, the texture is used (ascending, as in the design sketch) */
    looks: [
      { fromAge: 20, texture: TextureKey.FoxYoung },
      /** grey fur, glasses and a walking stick */
      { fromAge: 45, texture: TextureKey.FoxOld },
    ],
  },
  world: {
    scrollSpeed: 360,
    /** background moves at this fraction of the scroll speed (parallax) */
    skyParallax: 0.15,
    /** city skyline in front of the sky, behind the obstacles */
    skylineParallax: 0.4,
    skylineHeight: 300,
    groundHeight: 120,
  },
  /** small clouds drifting by slowly in the background, one after another (objects/Clouds.ts) */
  clouds: {
    /** artwork proportions (width : height) */
    aspect: [115, 70],
    /** width: far clouds are small … */
    minWidth: 70,
    /** … near ones a bit larger */
    maxWidth: 150,
    /** share of the scroll speed (parallax): far clouds drift slowest */
    minParallax: 0.05,
    maxParallax: 0.12,
    /** far clouds are a bit fainter */
    minAlpha: 0.7,
    /** vertical band (fraction of the playfield from the top) */
    minYFraction: 0.08,
    maxYFraction: 0.5,
    /** in view from the start */
    startCount: 2,
    /** a new cloud comes in from the right every … s of scrolling */
    gapMinSec: 4,
    gapMaxSec: 9,
  },
  pipes: {
    /** about a third of the figure's width, like the columns in the mockup of the facade (texture: scripts/build-art-assets.mjs) */
    width: 48,
    /**
     * Height of the opening ("gate"). Generous on purpose: on the projection every
     * input arrives with network delay, the player needs room to react.
     */
    gap: 400,
    /** gates for an old (less agile) player open a bit wider … */
    gapOld: 440,
    /** horizontal distance between two pipe pairs (left edge to left edge) */
    spacing: 640,
    /** minimum distance between gap and ceiling / ground */
    margin: 70,
    /** max. vertical jump of the gap centre between two consecutive pairs (young) … */
    maxGapShift: 300,
    /** … and old: smaller jumps, so the slower old bird can still make it */
    maxGapShiftOld: 190,
    /** distance from the right edge to the first pipe after the run starts */
    firstPipeOffset: 380,
  },
  /** Hitting a pipe costs one gold coin (and the subsidy that belongs to it). The round only ends with the time. */
  hits: {
    /** no further hits for this long after a hit (the bird passes through the pipe, blinking) */
    invulnerableSec: 1.2,
  },
  /**
   * Gold coins of one life (core/coinPlan.ts): exactly `TOTAL_COINS` (own contribution, shared/scoring.ts),
   * evenly spread – with room for the planes of the checkpoints.
   */
  coins: {
    displaySize: 90,
    /** the coin turns (spritesheet views) by this many degrees per second (one turn every two seconds) */
    rotationSpeed: 180,
    /** pick-up radius – a bit larger than the artwork, forgiving with input delay */
    hitRadius: 52,
    /** at least this far apart (WU) – a coin due too early is placed a little later */
    minDistance: 150,
    /** the coins follow the line between two gates, shifted up/down by up to this much (per stretch) */
    maxOffset: 130,
    /** no coins above this line – keeps them clear of the HUD (panels and progress bar) */
    minY: 240,
    /** the first coin reaches the figure this long after the start of the life … */
    firstSec: 1.5,
    /** … the last one at least this long before its end (with the maximum in reach: room for its bonus coin and plane) */
    lastSecBeforeEnd: 1,
    /** at least this long between two coins (short lives) */
    minGapSec: 0.45,
  },
  /**
   * Bonus coins = Grundzulage: earned by the gold coins (1:2, then 1:4, see BONUS_COIN_AFTER), they appear in
   * the next stretch not in view yet and are pulled to the figure (magnetic) – the subsidy is collected by itself.
   */
  bonus: {
    /** size relative to a gold coin */
    scale: 0.9,
    /** pulled to the figure from this distance on (WU ahead of it) … */
    magnetDx: 380,
    /** … at most this share of the way from the right edge to the figure (narrow phones: it lies in the course first) */
    magnetShare: 0.6,
    /** flight to the figure: acceleration and top speed (it starts with the speed of the course) */
    accel: 2600,
    maxSpeed: 1500,
  },
  /**
   * Special coins (shared/scoring.ts, SPECIAL_COINS): once a round each, collected by flying through them like a
   * gold coin. They reach the figure at this age (a quiet stretch for a plane moves them behind it).
   */
  special: {
    atAge: { starter: 23, baby: 32 },
    /** size relative to a gold coin – they stand out */
    scale: 1.3,
    /** they pulse instead of turning (the symbol stays readable) */
    pulse: 0.08,
    pulseSec: 0.6,
    /** never above this line (WU from the top): their name and amount pop up above them and stay clear of the top
     * of the window (on the facade its stepped corners would cut them off) */
    minY: 400,
  },
  /**
   * What the player sees when a coin is collected: the amount pops up above the coin in the colour of the coin
   * (above the figure, with a thick dark outline – readable on the light sky and on the facade) and pixel sparks
   * fly out. Sizes in WU, times in s.
   */
  feedback: {
    labelSize: 46,
    /** special coins: their name and amount – larger and longer in view, to be read */
    specialLabelSize: 54,
    specialSec: 2.2,
    specialHoldShare: 0.75,
    /** special coins: the label rises only a little (it stays low in the window) */
    specialRise: 50,
    /** the label rises this far, stays fully visible for `holdShare` of the time, then fades */
    rise: 120,
    sec: 1.0,
    holdShare: 0.55,
    sparks: 10,
    specialSparks: 28,
    sparkSize: 9,
    sparkSpeed: 420,
  },
  /** Start of a round on the facade: 3…2…1 right after START – the figure can fly through everything */
  countdown: {
    steps: 3,
  },
  /** Subsidy checkpoints (shared/scoring.ts): a plane with a banner crosses the window, no new coins meanwhile */
  checkpoint: {
    bannerSec: 4.5,
    /** new coins come into view again once the plane has flown this share of its way (1 = gone, banner included) … */
    coinsBackShare: 0.5,
    /** … + this */
    calmExtraSec: 0.4,
    /** height of the plane (fraction of the playfield from the top) */
    yFraction: 0.45,
  },
  /** Attract demo: the figure plays by itself */
  demo: {
    /** flap when the figure is this far below its target (fraction of the hit radius) */
    tolerance: 0.3,
    minFlapGapSec: 0.16,
  },
} as const;

export type Tuning = typeof TUNING;
